import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import {
  AdminShopCategory,
  createShopCategory,
  deleteShopCategory,
  reorderShopCategories,
  updateShopCategory,
} from "../../../api/shop";
import { Alert, Button, Card, Dialog, EmptyState, Field, IconButton, Input, Select, Tag, useDialogs } from "../../../ui";
import { codeFromName, normalizeCode } from "../shared/codes";
import { GuardedDelete } from "../shared/GuardedDelete";
import { ReorderControls } from "../shared/ReorderControls";
import { useLatched } from "../shared/useLatched";
import { PersistedOrder, usePersistedOrder } from "../shared/useReorder";
import { categoryDeleteBlock, plural, usedByLabel } from "./catalogGuards";
import { validateNamed } from "./catalogForms";
import type { CatalogTabProps } from "./types";

type CategoryForm = { name: string; code: string; codeTouched: boolean; parentId: string };
const EMPTY: CategoryForm = { name: "", code: "", codeTouched: false, parentId: "" };

type NodeProps = {
  category: AdminShopCategory;
  index: number;
  parentOrder: PersistedOrder<AdminShopCategory>;
  onAddSub: (parent: AdminShopCategory) => void;
  onEdit: (category: AdminShopCategory, parentId: string) => void;
  onDelete: (category: AdminShopCategory, isSub: boolean) => void;
  onChanged: CatalogTabProps["onChanged"];
  showError: CatalogTabProps["showError"];
};

/** A top-level category with its subcategories under it. Each level reorders on its own (siblings of one parent). */
function CategoryNode({ category, index, parentOrder, onAddSub, onEdit, onDelete, onChanged, showError }: NodeProps) {
  const subs = usePersistedOrder(
    category.subcategories,
    (c) => c.id,
    reorderShopCategories,
    (err) => showError(err, "Failed to reorder subcategories"),
    () => void onChanged()
  );
  return (
    <li className={parentOrder.dragIndex === index ? "reorder-dragging" : undefined} {...parentOrder.rowProps(index)}>
      <div className="catalog-row">
        <ReorderControls
          index={index}
          count={parentOrder.list.length}
          onStep={parentOrder.step}
          disabled={parentOrder.saving}
          handleProps={parentOrder.handleProps(index)}
        />
        <div className="catalog-row__main">
          <span className="catalog-row__name">{category.name}</span>
          <Tag className="code-tag">{category.code}</Tag>
        </div>
        <span className="catalog-row__meta">
          {category.subcategories.length > 0
            ? plural(category.subcategories.length, "subcategory", "subcategories")
            : usedByLabel(category.productCount)}
        </span>
        <div className="catalog-row__actions">
          <IconButton label="Add subcategory" icon={<Plus size={16} />} onClick={() => onAddSub(category)} />
          <IconButton label="Edit" icon={<Pencil size={16} />} onClick={() => onEdit(category, "")} />
          <GuardedDelete blockedReason={categoryDeleteBlock(category)} onDelete={() => onDelete(category, false)} />
        </div>
      </div>
      {subs.list.length > 0 && (
        <ul className="catalog-tree__children">
          {subs.list.map((sub, subIndex) => (
            <li key={sub.id} className={subs.dragIndex === subIndex ? "reorder-dragging" : undefined} {...subs.rowProps(subIndex)}>
              <div className="catalog-row">
                <ReorderControls
                  index={subIndex}
                  count={subs.list.length}
                  onStep={subs.step}
                  disabled={subs.saving}
                  handleProps={subs.handleProps(subIndex)}
                />
                <div className="catalog-row__main">
                  <span className="catalog-row__name">{sub.name}</span>
                  <Tag className="code-tag">{sub.code}</Tag>
                </div>
                <span className="catalog-row__meta">{usedByLabel(sub.productCount)}</span>
                <div className="catalog-row__actions">
                  <IconButton label="Edit" icon={<Pencil size={16} />} onClick={() => onEdit(sub, category.id)} />
                  <GuardedDelete blockedReason={categoryDeleteBlock(sub)} onDelete={() => onDelete(sub, true)} />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

const CategoriesTab = ({ catalog, onChanged, showError, showSuccess }: CatalogTabProps) => {
  const { confirm } = useDialogs();
  // A saved order is read back into the page's catalog, so it holds after switching tabs
  const top = usePersistedOrder(
    catalog.categories,
    (c) => c.id,
    reorderShopCategories,
    (err) => showError(err, "Failed to reorder categories"),
    () => void onChanged()
  );
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AdminShopCategory | null>(null);
  const [form, setForm] = useState<CategoryForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);

  const kind = form.parentId ? "Subcategory" : "Category";
  const title = useLatched(open, `${editing ? "Edit" : "Add"} ${kind}`);
  const saveLabel = useLatched(open, editing ? "Update" : "Add");
  const problem = validateNamed(form.name, form.code, "name");
  // Two levels only: a category with subcategories stays top-level, and a parent must be top-level
  const keepsTopLevel = !!editing && editing.subcategories.length > 0;
  const parentOptions = catalog.categories.filter((c) => c.id !== editing?.id);
  const parent = catalog.categories.find((c) => c.id === form.parentId);

  const openAdd = (parentId = "") => {
    setEditing(null);
    setForm({ ...EMPTY, parentId });
    setAttempted(false);
    setOpen(true);
  };

  const openEdit = (category: AdminShopCategory, parentId: string) => {
    setEditing(category);
    setForm({ name: category.name, code: category.code, codeTouched: true, parentId });
    setAttempted(false);
    setOpen(true);
  };

  const close = () => {
    if (!saving) setOpen(false);
  };

  const handleSave = async () => {
    setAttempted(true);
    if (problem) return;
    setSaving(true);
    try {
      const body = { name: form.name.trim(), code: form.code, parentId: form.parentId || null };
      if (editing) {
        await updateShopCategory(editing.id, body);
        showSuccess(`${kind} updated successfully`);
      } else {
        await createShopCategory(body);
        showSuccess(`${kind} added successfully`);
      }
      setOpen(false);
      await onChanged();
    } catch (err: any) {
      showError(err, `Failed to save ${kind.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category: AdminShopCategory, isSub: boolean) => {
    const what = isSub ? "Subcategory" : "Category";
    const ok = await confirm({
      title: `Delete ${what}`,
      tone: "danger",
      confirmLabel: "Delete",
      message: (
        <>
          Are you sure you want to delete "<strong>{category.name}</strong>"?
        </>
      ),
    });
    if (!ok) return;
    try {
      await deleteShopCategory(category.id);
      showSuccess(`${what} deleted successfully`);
      await onChanged();
    } catch (err: any) {
      showError(err, `Failed to delete ${what.toLowerCase()}`);
    }
  };

  return (
    <>
      <Card
        padding="none"
        title="Categories"
        subtitle="Categories and their subcategories (two levels). A product is filed under the most specific one."
        actions={
          <Button size="sm" icon={<Plus size={16} />} onClick={() => openAdd()}>
            Add Category
          </Button>
        }
      >
        {top.list.length === 0 ? (
          <EmptyState compact title="No categories yet." description="Add Apparel, Gear… then their subcategories." />
        ) : (
          <ul className="catalog-list catalog-tree">
            {top.list.map((category, index) => (
              <CategoryNode
                key={category.id}
                category={category}
                index={index}
                parentOrder={top}
                onAddSub={(p) => openAdd(p.id)}
                onEdit={openEdit}
                onDelete={handleDelete}
                onChanged={onChanged}
                showError={showError}
              />
            ))}
          </ul>
        )}
      </Card>

      <Dialog
        open={open}
        onClose={close}
        title={title}
        size="sm"
        busy={saving}
        bodyClassName="catalog-form"
        footer={
          <>
            <Button variant="ghost" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving} disabled={!form.name.trim()}>
              {saveLabel}
            </Button>
          </>
        }
      >
        <Field label="Name" required>
          {(id) => (
            <Input
              id={id}
              value={form.name}
              maxLength={100}
              placeholder={form.parentId ? "e.g. T-shirts" : "e.g. Apparel"}
              onChange={(e) => {
                const name = e.target.value;
                setForm((f) => ({ ...f, name, code: f.codeTouched ? f.code : codeFromName(name) }));
              }}
            />
          )}
        </Field>
        <Field label="Code" required hint="1–6 letters or digits, used in product codes (PAD-TSH-01). Changing it never rewrites existing SKUs.">
          {(id) => (
            <Input
              id={id}
              value={form.code}
              maxLength={6}
              placeholder="e.g. TSH"
              onChange={(e) => {
                const code = normalizeCode(e.target.value);
                setForm((f) => ({ ...f, code, codeTouched: true }));
              }}
            />
          )}
        </Field>
        <Field
          label="Parent"
          hint={keepsTopLevel ? "It has subcategories, so it stays a top-level category" : "Leave it empty for a top-level category"}
        >
          {(id) => (
            <Select
              id={id}
              block
              value={form.parentId}
              disabled={keepsTopLevel}
              onChange={(e) => {
                const parentId = e.target.value;
                setForm((f) => ({ ...f, parentId }));
              }}
            >
              <option value="">None (top-level category)</option>
              {parentOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {parent && parent.subcategories.length === 0 && parent.productCount > 0 && (
          <Alert tone="warn">
            {parent.name} has {plural(parent.productCount, "product", "products")} filed under it. Once it has subcategories,
            each of them needs a subcategory the next time it's saved.
          </Alert>
        )}
        {attempted && problem && <Alert tone="danger">{problem}</Alert>}
      </Dialog>
    </>
  );
};

export default CategoriesTab;
